import { observable, configure, action, computed } from 'mobx';
import LocalModel from './localModel';

import util from 'preact-util';
import { route } from 'preact-router';
import PubSub, { topics } from '../lib/pubsub';

import md5 from 'crypto-js/md5';

configure({ enforceActions: 'always' });

class CourseStore extends LocalModel {
    constructor() {
        super('course', {
            namePlural: 'courses',
            namePluralReal: 'courses',
            sort: '-startDate',
            limit: 100,
            api: {
                search: {
                    url: '/api/courses/',
                    params: {
                        limit: 15,
                        sort: '-startDate',
                    },
                },
                load: {
                    url: '/api/courses/',
                    params: {},
                },
                save: {
                    url: '/api/courses/',
                    params: {},
                },
                delete: {
                    url: '/api/courses/',
                    params: {},
                },
            },
        });
    }

    // NOTE: `currentHostname` is the base StoreModel observable, filled by
    // app.js `courseStore.setVersion(versionData, hostname)`. Do NOT add a
    // currentHostname getter here: mobx 5 installs an own instance property
    // for the base field on first observable access, which shadows a subclass
    // getter and silently sends `hostname=0`.

    @observable newCourse = {};

    @observable course = {};

    @observable courses = [];

    @observable tags = [];

    @observable signupInProgress = false;

    @observable signupError = null;

    @observable signupSuccess = null;

    /** Exams the current user may pick for a course (own + invited, as the API scopes them). */
    @observable examOptions = [];

    @action
    async loadExamOptions() {
        const response = await util.fetchApi('/api/exams/', { method: 'GET' }, { limit: 200, sort: '-createdDate' });
        if (response?.status === 200) {
            this.updateKeyValue('examOptions', (response.data || []).map(e => ({ id: e.id, title: e.title, isActive: e.isActive })));
        }
        return response;
    }

    /**
     * Invite every participant of a course to its linked exam.
     * @param {number} courseId
     * @param {Date|string|null} deadline - overrides the exam's default deadline
     */
    @action
    async sendExam(courseId, deadline = null) {
        const body = {};
        if (deadline) {
            body.deadline = deadline instanceof Date ? deadline.toISOString() : deadline;
        }
        return util.fetchApi(`/api/courses/${courseId}/send-exam`, { method: 'POST' }, body);
    }

    /**
     * Add a new date to the course
     * @param {Date} startTime - Start date/time
     * @param {Date} endTime - End date/time (optional)
     * @param {number} slots - Number of available slots (0 = unlimited)
     * @param {string} title - Optional title for this date
     * @param {number} price - Optional price override
     * @param {string} description - Optional description
     * @param {string} location - Optional location name
     * @param {number} lat - Optional latitude
     * @param {number} lon - Optional longitude
     */
    async addDate({ startTime, endTime, slots = 0, title = '', price = null, description = '', location = '', lat = null, lon = null }) {
        if (!this.course.id) {
            console.error('No course loaded');
            return;
        }

        const newDate = {
            startTime: startTime,
            endTime: endTime || null,
            slots: slots || 0,
            title: title || '',
            price: price || null,
            description: description || '',
            location: location || '',
            lat: lat || null,
            lon: lon || null,
        };
        newDate.md5 = md5(JSON.stringify({ ...newDate, timestamp: Date.now() })).toString();

        const response = await this.save({
            id: this.course.id,
            dates: newDate,
        });

        return response;
    }

    /**
     * Remove a date from the course
     * @param {string} dateMd5 - The MD5 hash of the date to remove
     */
    async removeDate(dateMd5) {
        if (!this.course.id || !dateMd5) {
            return;
        }

        const response = await util.fetchApi(`/api/courses/${this.course.id}`, { method: 'PATCH' }, {
            removeDatesByMd5: dateMd5,
        });

        return response;
    }

    /**
     * Sign up current user for a course date
     * @param {string} dateMd5 - The MD5 hash of the date to sign up for
     * @returns {Object} - API response with saleId if successful
     */
    @action
    async signUpForDate(dateMd5) {
        if (!this.course.id || !dateMd5) {
            return { status: 400, errorCode: 'MISSING_DATA', message: 'Missing course or date' };
        }

        this.updateKeyValue('signupInProgress', true);
        this.updateKeyValue('signupError', null);
        this.updateKeyValue('signupSuccess', null);

        try {
            const response = await util.fetchApi(
                `/api/courses/${this.course.id}/date-signup`,
                { method: 'POST' },
                { dateMd5 }
            );

            if (response.status === 200) {
                this.updateKeyValue('signupSuccess', response.message || 'Du er nå påmeldt kurset');
                // Reload course to get updated participants
                await this.load(this.course.id);
            } else {
                this.updateKeyValue('signupError', response.message || 'Kunne ikke melde deg på');
            }

            return response;
        } catch (error) {
            console.error('Signup error:', error);
            this.updateKeyValue('signupError', 'En feil oppstod under påmelding');
            return { status: 500, errorCode: 'ERROR', message: 'En feil oppstod' };
        } finally {
            this.updateKeyValue('signupInProgress', false);
        }
    }

    /**
     * Cancel signup for a course date
     * @param {string} dateMd5 - The MD5 hash of the date to cancel
     * @returns {Object} - API response
     */
    @action
    async cancelSignup(dateMd5) {
        if (!this.course.id || !dateMd5) {
            return { status: 400, errorCode: 'MISSING_DATA', message: 'Missing course or date' };
        }

        this.updateKeyValue('signupInProgress', true);
        this.updateKeyValue('signupError', null);
        this.updateKeyValue('signupSuccess', null);

        try {
            const response = await util.fetchApi(
                `/api/courses/${this.course.id}/date-signup`,
                { method: 'DELETE' },
                { dateMd5 }
            );

            if (response.status === 200) {
                this.updateKeyValue('signupSuccess', response.message || 'Du er nå avmeldt kurset');
                // Reload course to get updated participants
                await this.load(this.course.id);
            } else {
                this.updateKeyValue('signupError', response.message || 'Kunne ikke avmelde deg');
            }

            return response;
        } catch (error) {
            console.error('Cancel error:', error);
            this.updateKeyValue('signupError', 'En feil oppstod under avmelding');
            return { status: 500, errorCode: 'ERROR', message: 'En feil oppstod' };
        } finally {
            this.updateKeyValue('signupInProgress', false);
        }
    }

    /**
     * Check if a user is signed up for a specific date
     * @param {number} userId - The user ID to check
     * @param {string} dateMd5 - The MD5 hash of the date
     * @returns {Object|null} - Participant object if signed up, null otherwise
     */
    isUserSignedUpForDate(userId, dateMd5) {
        const dates = this.course.dates || [];
        const dateIdx = dates.findIndex(d => d.md5 === dateMd5);
        if (dateIdx < 0) return null;

        const dateNumber = dateIdx + 1;
        const participantsFieldName = `participants${dateNumber}`;
        const participants = this.course[participantsFieldName] || [];

        return participants.find(p => p.id === userId) || null;
    }

    /**
     * Get all dates a user is signed up for
     * @param {number} userId - The user ID to check
     * @returns {Array} - Array of dates the user is signed up for with signup info
     */
    getUserSignedUpDates(userId) {
        const dates = this.course.dates || [];
        const signedUpDates = [];

        dates.forEach((date, idx) => {
            const dateNumber = idx + 1;
            const participantsFieldName = `participants${dateNumber}`;
            const participants = this.course[participantsFieldName] || [];
            const userParticipant = participants.find(p => p.id === userId);

            if (userParticipant) {
                signedUpDates.push({
                    ...date,
                    dateNumber,
                    participantsFieldName,
                    signup: userParticipant,
                });
            }
        });

        return signedUpDates;
    }

    /**
     * Get available dates for a user (with signup status and availability info)
     * @param {number} userId - The user ID to check
     * @returns {Array} - Array of dates with availability info
     */
    getAvailableDatesForUser(userId) {
        const dates = this.course.dates || [];

        return dates.map((date, idx) => {
            const dateNumber = idx + 1;
            const participantsFieldName = `participants${dateNumber}`;
            const participants = this.course[participantsFieldName] || [];
            const slotsUsed = participants.length;
            const slotsTotal = date.slots || 0;
            const userParticipant = participants.find(p => p.id === userId);
            const isUserSignedUp = !!userParticipant;

            const isFull = slotsTotal > 0 && slotsUsed >= slotsTotal;
            const canSignUp = !isUserSignedUp && !isFull;

            return {
                ...date,
                dateNumber,
                participantsFieldName,
                slotsUsed,
                slotsTotal,
                isUserSignedUp,
                userSignup: userParticipant || null,
                isFull,
                canSignUp,
            };
        });
    }

    /**
     * Generate preview of dates for bulk generation
     * @param {Object} params - Generation parameters
     * @param {Date} params.startTime - First date start time
     * @param {Date} params.endTime - Last date end time
     * @param {number} params.intervalDays - Days between dates
     * @returns {number} - Number of dates that would be generated
     */
    calculateDatePreview({ startTime, endTime, intervalDays }) {
        if (!startTime || !endTime || !intervalDays || intervalDays <= 0) {
            return 0;
        }
        const start = new Date(startTime);
        const end = new Date(endTime);
        if (end <= start) {
            return 0;
        }
        const diffMs = end.getTime() - start.getTime();
        const diffDays = diffMs / (1000 * 60 * 60 * 24);
        return Math.floor(diffDays / intervalDays) + 1;
    }

    /**
     * Bulk generate course dates
     * @param {Object} params - Generation parameters
     * @param {Date} params.startTime - First date start time
     * @param {Date} params.endTime - Last date end time
     * @param {number} params.intervalDays - Days between dates
     * @param {number} params.slots - Slots per date
     * @param {number} params.price - Price per date (optional)
     */
    async bulkGenerateDates({ startTime, endTime, intervalDays, slots = 0, price = null }) {
        if (!startTime || !endTime) {
            throw new Error('Start time and end time are required');
        }
        if (!intervalDays || intervalDays <= 0) {
            throw new Error('Interval must be greater than 0');
        }

        const start = new Date(startTime);
        const end = new Date(endTime);
        if (end <= start) {
            throw new Error('End time must be after start time');
        }

        // Get existing dates
        const existingDates = this.course.dates || [];

        // Generate new dates
        const newDates = [];
        let currentDate = new Date(start);

        while (currentDate <= end) {
            const newDate = {
                startTime: new Date(currentDate),
                endTime: null,
                slots: slots || 0,
                title: '',
                price: price || null,
                description: '',
                location: '',
                lat: null,
                lon: null,
            };
            newDate.md5 = md5(JSON.stringify({ ...newDate, timestamp: Date.now(), index: newDates.length })).toString();

            newDates.push(newDate);

            // Move to next interval
            currentDate.setDate(currentDate.getDate() + intervalDays);
        }

        // Combine existing dates with new dates
        const allDates = [...existingDates, ...newDates];

        // Save using dates-overwrite pattern
        const response = await this.save({
            id: this.course.id,
            'dates-overwrite': allDates,
        });

        return response;
    }

    @action
    clearMessages() {
        this.updateKeyValue('signupError', null);
        this.updateKeyValue('signupSuccess', null);
    }

    /**
     * Admin: Add a participant to the course or a specific date
     * @param {Object} user - User object with id, firstname, lastname, email, cellphone
     * @param {string} dateMd5 - Optional MD5 hash of the date to add participant to
     * @returns {Object} - API response
     */
    async adminAddParticipant(user, dateMd5 = null) {
        if (!this.course.id || !user?.id) {
            return { status: 400, message: 'Missing course or user' };
        }

        let participantsFieldName = 'participants';
        if (dateMd5) {
            const dates = this.course.dates || [];
            const dateIdx = dates.findIndex(d => d.md5 === dateMd5);
            if (dateIdx >= 0) {
                participantsFieldName = `participants${dateIdx + 1}`;
            }
        }

        const participant = {
            id: user.id,
            firstname: user.firstname,
            lastname: user.lastname,
            email: user.email,
            cellphone: user.cellphone,
            md5: md5(JSON.stringify({ id: user.id, timestamp: Date.now() })).toString(),
            signupDate: new Date(),
            addedByAdmin: true,
        };

        return await this.save({
            id: this.course.id,
            [participantsFieldName]: participant,
        });
    }

    /**
     * Admin: Remove a participant from the course or a specific date
     * @param {string} participantMd5 - The MD5 hash of the participant to remove
     * @param {string} participantsFieldName - The field name (e.g., 'participants', 'participants1')
     * @returns {Object} - API response
     */
    async adminRemoveParticipant(participantMd5, participantsFieldName = 'participants') {
        if (!this.course.id || !participantMd5) {
            return { status: 400, message: 'Missing course or participant' };
        }

        const removeField = `remove${util.ucfirst(participantsFieldName)}ByMd5`;

        return await util.fetchApi(`/api/courses/${this.course.id}`, { method: 'PATCH' }, {
            [removeField]: participantMd5,
        });
    }

    /**
     * Sign up current user for the entire course (course-level signup)
     * @param {Object} signupData - Optional custom field data from signup form
     * @returns {Object} - API response with saleId if successful
     */
    @action
    async signUpForCourse(signupData = {}) {
        if (!this.course.id) {
            return { status: 400, errorCode: 'MISSING_DATA', message: 'Missing course' };
        }

        this.updateKeyValue('signupInProgress', true);
        this.updateKeyValue('signupError', null);
        this.updateKeyValue('signupSuccess', null);

        try {
            const response = await util.fetchApi(
                `/api/courses/${this.course.id}/date-signup`,
                { method: 'POST' },
                { signupData }  // Pass custom field data
            );

            if (response.status === 200) {
                this.updateKeyValue('signupSuccess', response.message || 'Du er nå påmeldt kurset');
                // Reload course to get updated participants
                await this.load(this.course.id);
            } else {
                this.updateKeyValue('signupError', response.message || 'Kunne ikke melde deg på');
            }

            return response;
        } catch (error) {
            console.error('Signup error:', error);
            this.updateKeyValue('signupError', 'En feil oppstod under påmelding');
            return { status: 500, errorCode: 'ERROR', message: 'En feil oppstod' };
        } finally {
            this.updateKeyValue('signupInProgress', false);
        }
    }

    /**
     * Cancel course-level signup
     * @returns {Object} - API response
     */
    @action
    async cancelCourseSignup() {
        if (!this.course.id) {
            return { status: 400, errorCode: 'MISSING_DATA', message: 'Missing course' };
        }

        this.updateKeyValue('signupInProgress', true);
        this.updateKeyValue('signupError', null);
        this.updateKeyValue('signupSuccess', null);

        try {
            const response = await util.fetchApi(
                `/api/courses/${this.course.id}/date-signup`,
                { method: 'DELETE' },
                {}  // No dateMd5 = course-level cancellation
            );

            if (response.status === 200) {
                this.updateKeyValue('signupSuccess', response.message || 'Du er nå avmeldt kurset');
                // Reload course to get updated participants
                await this.load(this.course.id);
            } else {
                this.updateKeyValue('signupError', response.message || 'Kunne ikke avmelde deg');
            }

            return response;
        } catch (error) {
            console.error('Cancel error:', error);
            this.updateKeyValue('signupError', 'En feil oppstod under avmelding');
            return { status: 500, errorCode: 'ERROR', message: 'En feil oppstod' };
        } finally {
            this.updateKeyValue('signupInProgress', false);
        }
    }

    /**
     * Check if a user is signed up for the entire course (course-level)
     * @param {number} userId - The user ID to check
     * @returns {Object|null} - Participant object if signed up, null otherwise
     */
    isUserSignedUpForCourse(userId) {
        const participants = this.course.participants || [];
        return participants.find(p => p.id === userId) || null;
    }

    /**
     * Get course availability info for a user
     * @param {number} userId - The user ID to check
     * @returns {Object} - Availability info
     */
    getCourseAvailability(userId) {
        const participants = this.course.participants || [];
        const slotsUsed = participants.length;
        const slotsTotal = this.course.slots || 0;
        const userParticipant = participants.find(p => p.id === userId);
        const isSignedUp = !!userParticipant;
        const isFull = slotsTotal > 0 && slotsUsed >= slotsTotal;
        const canSignUp = !isSignedUp && !isFull;

        return {
            slotsUsed,
            slotsTotal,
            isSignedUp,
            userSignup: userParticipant || null,
            isFull,
            canSignUp,
        };
    }
}

const store = new CourseStore();
export default store;
